import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PaymentDistributionPage = () => {
  const { currentUser } = useAuth();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [approvedPayments, setApprovedPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [wallets, setWallets] = useState([]);
  
  // For distribution planning
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [distributionPlan, setDistributionPlan] = useState([{ wallet_id: '', amount_sol: '' }]);
  const [showDistributionForm, setShowDistributionForm] = useState(false);
  
  // For executing distribution
  const [executingDistribution, setExecutingDistribution] = useState(null);
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch pending payments
        const pendingResponse = await api.get('/payments/pending');
        setPendingPayments(pendingResponse.data);
        
        // Fetch approved payments
        const approvedResponse = await api.get('/payments/approved');
        setApprovedPayments(approvedResponse.data);
        
        // Fetch wallets for distribution
        const walletsResponse = await api.get('/wallets');
        setWallets(walletsResponse.data.wallets || walletsResponse.data);
      } catch (err) {
        console.error('Error fetching payment data:', err);
        setError('Failed to load payment data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Set up polling for updates
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, []);
  
  // Approve payment
  const handleApprove = async (paymentId) => {
    if (actionInProgress) return;
    
    setActionInProgress(true);
    
    try {
      const response = await api.post(`/payments/${paymentId}/approve`);
      
      if (response.data.success) {
        // Update state by removing from pending and adding to approved
        const payment = pendingPayments.find(p => p.id === paymentId);
        setPendingPayments(prev => prev.filter(p => p.id !== paymentId));
        
        const updatedPayment = {
          ...payment,
          ...response.data.payment
        };
        
        setApprovedPayments(prev => [updatedPayment, ...prev]);
      }
    } catch (err) {
      console.error('Error approving payment:', err);
      setError('Failed to approve payment. Please try again.');
    } finally {
      setActionInProgress(false);
    }
  };
  
  // Start planning a distribution
  const handlePlanDistribution = (payment) => {
    setSelectedPayment(payment);
    setShowDistributionForm(true);
    
    // Initialize distribution plan with a single empty entry
    setDistributionPlan([{ wallet_id: '', amount_sol: '' }]);
  };
  
  // Add wallet to distribution plan
  const addWalletToDistribution = () => {
    setDistributionPlan(prev => [...prev, { wallet_id: '', amount_sol: '' }]);
  };
  
  // Remove wallet from distribution plan
  const removeWalletFromDistribution = (index) => {
    setDistributionPlan(prev => prev.filter((_, i) => i !== index));
  };
  
  // Update distribution plan
  const updateDistributionPlan = (index, field, value) => {
    setDistributionPlan(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    );
  };
  
  // Calculate total SOL in distribution plan
  const calculateTotalSol = () => {
    return distributionPlan.reduce((total, item) => {
      const amount = parseFloat(item.amount_sol) || 0;
      return total + amount;
    }, 0);
  };
  
  // Submit distribution plan
  const submitDistributionPlan = async () => {
    if (actionInProgress || !selectedPayment) return;
    
    // Validate distribution plan
    const validDistributions = distributionPlan.filter(
      item => item.wallet_id && parseFloat(item.amount_sol) > 0
    );
    
    if (validDistributions.length === 0) {
      setError('Please add at least one valid wallet with a positive SOL amount');
      return;
    }
    
    const totalSol = calculateTotalSol();
    if (totalSol > parseFloat(selectedPayment.amount_sol)) {
      setError(`Total SOL (${totalSol}) exceeds available amount (${selectedPayment.amount_sol})`);
      return;
    }
    
    setActionInProgress(true);
    
    try {
      const response = await api.post(`/payments/${selectedPayment.id}/plan-distribution`, {
        distributions: validDistributions
      });
      
      if (response.data.success) {
        // Update the payment with new distributions
        const updatedPayments = approvedPayments.map(payment => 
          payment.id === selectedPayment.id 
            ? { 
                ...payment, 
                distributions: response.data.distributions,
                distribution_status: 'planned'
              } 
            : payment
        );
        
        setApprovedPayments(updatedPayments);
        setShowDistributionForm(false);
        setSelectedPayment(null);
      }
    } catch (err) {
      console.error('Error submitting distribution plan:', err);
      setError('Failed to submit distribution plan. Please try again.');
    } finally {
      setActionInProgress(false);
    }
  };
  
  // Execute a single distribution
  const executeDistribution = async (distributionId) => {
    if (actionInProgress) return;
    
    setExecutingDistribution(distributionId);
    setActionInProgress(true);
    
    try {
      const response = await api.post(`/payments/distribution/${distributionId}/execute`);
      
      if (response.data.success) {
        // Refresh the approved payments to show updated status
        const refreshResponse = await api.get('/payments/approved');
        setApprovedPayments(refreshResponse.data);
      }
    } catch (err) {
      console.error('Error executing distribution:', err);
      setError(`Failed to execute distribution: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionInProgress(false);
      setExecutingDistribution(null);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Payment Distribution Management</h1>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p>{error}</p>
          <button 
            className="text-red-700 font-bold ml-2"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Distribution Plan Form */}
      {showDistributionForm && selectedPayment && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Plan SOL Distribution - Payment #{selectedPayment.id}
            </h2>
            
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Amount USD</p>
                  <p className="font-bold">${parseFloat(selectedPayment.amount_usd).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Amount SOL</p>
                  <p className="font-bold">{parseFloat(selectedPayment.amount_sol).toFixed(4)} SOL</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Exchange Rate</p>
                  <p className="font-bold">1 SOL = ${parseFloat(selectedPayment.exchange_rate).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-bold capitalize">{selectedPayment.status}</p>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-bold mb-2">Distribution Plan</h3>
              
              {distributionPlan.map((item, index) => (
                <div key={index} className="flex items-center space-x-4 mb-2">
                  <div className="flex-1">
                    <select
                      value={item.wallet_id}
                      onChange={(e) => updateDistributionPlan(index, 'wallet_id', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select a wallet</option>
                      {wallets.map(wallet => (
                        <option key={wallet.id} value={wallet.id}>
                          {wallet.name || wallet.address.substring(0, 6) + '...' + wallet.address.substring(wallet.address.length - 4)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      step="0.0001"
                      value={item.amount_sol}
                      onChange={(e) => updateDistributionPlan(index, 'amount_sol', e.target.value)}
                      placeholder="SOL amount"
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <button
                    onClick={() => removeWalletFromDistribution(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ))}
              
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={addWalletToDistribution}
                  className="flex items-center text-purple-600 hover:text-purple-800"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Another Wallet
                </button>
                
                <div className="text-right">
                  <p className="text-sm text-gray-600">Total SOL to Distribute</p>
                  <p className="font-bold">{calculateTotalSol().toFixed(4)} / {parseFloat(selectedPayment.amount_sol).toFixed(4)} SOL</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowDistributionForm(false);
                  setSelectedPayment(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={submitDistributionPlan}
                disabled={actionInProgress}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                {actionInProgress ? 'Saving...' : 'Save Distribution Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Pending Payments */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Pending Payments</h2>
        
        {pendingPayments.length === 0 ? (
          <p className="text-gray-500">No pending payments found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 text-left text-gray-600">ID</th>
                  <th className="px-4 py-2 text-left text-gray-600">Amount (USD)</th>
                  <th className="px-4 py-2 text-left text-gray-600">Status</th>
                  <th className="px-4 py-2 text-left text-gray-600">Created At</th>
                  <th className="px-4 py-2 text-left text-gray-600">Payer</th>
                  <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayments.map(payment => (
                  <tr key={payment.id} className="border-b">
                    <td className="px-4 py-2">{payment.id}</td>
                    <td className="px-4 py-2">${parseFloat(payment.amount_usd).toFixed(2)}</td>
                    <td className="px-4 py-2 capitalize">{payment.status}</td>
                    <td className="px-4 py-2">{formatDate(payment.created_at)}</td>
                    <td className="px-4 py-2">{payment.payer_email || 'N/A'}</td>
                    <td className="px-4 py-2">
                      {payment.status === 'paid' && (
                        <button
                          onClick={() => handleApprove(payment.id)}
                          disabled={actionInProgress}
                          className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Approved Payments */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Approved Payments</h2>
        
        {approvedPayments.length === 0 ? (
          <p className="text-gray-500">No approved payments found.</p>
        ) : (
          <div className="space-y-6">
            {approvedPayments.map(payment => (
              <div key={payment.id} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold">
                        Payment #{payment.id} - ${parseFloat(payment.amount_usd).toFixed(2)} USD
                      </h3>
                      <p className="text-sm text-gray-600">
                        {parseFloat(payment.amount_sol).toFixed(4)} SOL | 
                        Exchange Rate: ${parseFloat(payment.exchange_rate).toFixed(2)} |
                        Approved: {formatDate(payment.approved_at)}
                      </p>
                    </div>
                    <div>
                      {(!payment.distributions || payment.distributions.length === 0) && (
                        <button
                          onClick={() => handlePlanDistribution(payment)}
                          className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                        >
                          Plan Distribution
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Distributions */}
                {payment.distributions && payment.distributions.length > 0 && (
                  <div className="p-4">
                    <h4 className="font-bold mb-2">Distributions</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 text-left text-gray-600">ID</th>
                            <th className="px-4 py-2 text-left text-gray-600">Wallet</th>
                            <th className="px-4 py-2 text-left text-gray-600">Amount (SOL)</th>
                            <th className="px-4 py-2 text-left text-gray-600">Status</th>
                            <th className="px-4 py-2 text-left text-gray-600">Transaction Hash</th>
                            <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payment.distributions.map(dist => (
                            <tr key={dist.id} className="border-b">
                              <td className="px-4 py-2">{dist.id}</td>
                              <td className="px-4 py-2">
                                {dist.wallet_name || 'N/A'}<br />
                                <span className="text-xs text-gray-500">
                                  {dist.wallet_address ? (
                                    `${dist.wallet_address.substring(0, 6)}...${dist.wallet_address.substring(dist.wallet_address.length - 4)}`
                                  ) : 'N/A'}
                                </span>
                              </td>
                              <td className="px-4 py-2">{parseFloat(dist.amount_sol).toFixed(4)}</td>
                              <td className="px-4 py-2 capitalize">{dist.status}</td>
                              <td className="px-4 py-2">
                                {dist.tx_hash ? (
                                  <a 
                                    href={`https://explorer.solana.com/tx/${dist.tx_hash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {dist.tx_hash.substring(0, 8)}...
                                  </a>
                                ) : 'N/A'}
                              </td>
                              <td className="px-4 py-2">
                                {dist.status === 'pending' && (
                                  <button
                                    onClick={() => executeDistribution(dist.id)}
                                    disabled={actionInProgress || executingDistribution === dist.id}
                                    className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                                  >
                                    {executingDistribution === dist.id ? 'Executing...' : 'Execute'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDistributionPage;